import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Mapping } from '@atlaskit/editor-prosemirror/transform';

export type InlineRovoEditTargetRange = {
	from: number;
	to: number;
};

/**
 * Finds the first node carrying `localId`, or `undefined` when the document has none.
 *
 * This is a full document traversal. Inside `Plugin.apply` it must only run as one-time
 * initialisation for an attempt or as the fallback when a mapped position no longer holds the
 * target (see `trackInlineRovoEditTarget`).
 */
export function findNodeByLocalId(
	doc: PMNode,
	localId: string,
): { node: PMNode; pos: number } | undefined {
	let found: { node: PMNode; pos: number } | undefined;
	doc.descendants((node, pos) => {
		if (found) {
			return false;
		}
		if (node.attrs?.localId === localId) {
			found = { node, pos };
			return false;
		}
		return true;
	});
	return found;
}

/**
 * Decides whether an inline Rovo edit's target node changed identity between two snapshots.
 *
 * The rule is deliberately narrower than range overlap (CCCEMAU-3495's `savedSelection`
 * detector) so that a resize, which only rewrites the `mediaSingle` wrapper's attributes, does
 * not cancel the edit (product decision, MAUI-979):
 * - the target is gone (deleted, cut, or converted: a new wrapper gets a new localId) → changed;
 * - the target's node type changed → changed;
 * - for a `mediaSingle` target, the `media` child differs in attrs or marks (border, alt, file id)
 *   → changed; wrapper-only attribute changes (width, layout, widthType) → unchanged;
 * - for any other target (MAUI embeds), only removal or a type change counts.
 */
export function hasInlineRovoEditTargetNodeChanged(
	before: PMNode,
	after: PMNode | undefined,
): boolean {
	if (!after) {
		return true;
	}
	if (after.type !== before.type) {
		return true;
	}
	if (before.type.name === 'mediaSingle') {
		const beforeMedia = before.firstChild;
		const afterMedia = after.firstChild;
		return !beforeMedia || !afterMedia || !beforeMedia.eq(afterMedia);
	}
	return false;
}

/**
 * Full-scan form of the identity rule: resolves the target in both documents by localId.
 *
 * A target that is absent from the *old* document is reported as unchanged: the mutation that
 * removed it was a different transaction, and unrelated later edits must not cancel anything.
 * Prefer `trackInlineRovoEditTarget` on hot paths; this walks both documents.
 */
export function hasInlineRovoEditTargetChanged(
	oldDoc: PMNode,
	newDoc: PMNode,
	localId: string,
): boolean {
	const before = findNodeByLocalId(oldDoc, localId);
	if (!before) {
		return false;
	}
	return hasInlineRovoEditTargetNodeChanged(before.node, findNodeByLocalId(newDoc, localId)?.node);
}

const rangeOf = ({ node, pos }: { node: PMNode; pos: number }): InlineRovoEditTargetRange => ({
	from: pos,
	to: pos + node.nodeSize,
});

/**
 * Follows an inline edit's target node across one document-changing transaction without walking
 * the document in the common case.
 *
 * - `range` is the target's range in `oldDoc` from the previous transaction. When it is absent (a
 *   newly admitted attempt) the target is resolved from `oldDoc` once.
 * - When no step touched the range (`isRangeTouched`), the range is remapped through `mapping`
 *   and the target is reported unchanged. This is O(steps).
 * - When a step touched the range, the node at the mapped position is compared with the old node
 *   using the identity rule. If the mapped position no longer holds the target's localId (a move,
 *   or a deletion with other content sliding in), one guarded re-scan of `newDoc` locates it.
 *
 * Returns the range to carry into the next transaction (`undefined` when the target is gone) and
 * whether the target changed identity.
 */
export function trackInlineRovoEditTarget({
	isRangeTouched,
	localId,
	mapping,
	newDoc,
	oldDoc,
	range,
}: {
	isRangeTouched: (range: InlineRovoEditTargetRange) => boolean;
	localId: string;
	mapping: Mapping;
	newDoc: PMNode;
	oldDoc: PMNode;
	range: InlineRovoEditTargetRange | undefined;
}): { changed: boolean; range: InlineRovoEditTargetRange | undefined } {
	const current =
		range ??
		(() => {
			const resolved = findNodeByLocalId(oldDoc, localId);
			return resolved ? rangeOf(resolved) : undefined;
		})();
	if (!current) {
		// Not in the old document: nothing to compare against for this transaction.
		return { changed: false, range: undefined };
	}

	const mapped: InlineRovoEditTargetRange = {
		from: mapping.map(current.from, 1),
		to: mapping.map(current.to, -1),
	};
	if (!isRangeTouched(current)) {
		return { changed: false, range: mapped };
	}

	const before = oldDoc.nodeAt(current.from);
	if (!before || before.attrs?.localId !== localId) {
		// The cached range drifted from the target; re-anchor from scratch on the next transaction.
		return { changed: false, range: undefined };
	}

	const atMapped =
		mapped.to > mapped.from && mapped.from < newDoc.content.size
			? newDoc.nodeAt(mapped.from)
			: null;
	const after =
		atMapped && atMapped.attrs?.localId === localId
			? { node: atMapped, pos: mapped.from }
			: findNodeByLocalId(newDoc, localId);

	return {
		changed: hasInlineRovoEditTargetNodeChanged(before, after?.node),
		range: after ? rangeOf(after) : undefined,
	};
}
